import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { BadgeCheck, Clock, Loader2, ShieldCheck } from "lucide-react";

const VerificationCard = ({ token }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) return;
    refresh();
  }, [token]);

  const refresh = async () => {
    try {
      const res = await axios.get(`${API}/artists/me/verification-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(res.data);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  const requestVerification = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/artists/request-verification`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Verification request submitted! We'll review within 48 hours.");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit request");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="card p-6"><div className="h-24 animate-pulse bg-[#1A2030] rounded" /></div>;
  if (!status?.has_artist_profile) return null;

  return (
    <div className="card p-6" data-testid="verification-card">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-[#EC008C]/15 border border-[#EC008C]/30 flex items-center justify-center flex-shrink-0">
          {status.is_verified ? <BadgeCheck className="w-6 h-6 text-[#EC008C]" /> : <ShieldCheck className="w-6 h-6 text-[#EC008C]" />}
        </div>
        <div className="flex-1">
          <h3 className="font-['Unbounded'] text-lg font-bold text-white mb-1">Artist Verification</h3>
          {status.is_verified ? (
            <p className="text-sm text-[#8DB87A] flex items-center gap-1">
              <BadgeCheck className="w-4 h-4" /> Your account is verified. The gold badge appears on your profile.
            </p>
          ) : status.request_status === "pending" ? (
            <div>
              <p className="text-sm text-[#EC008C] flex items-center gap-1 mb-1">
                <Clock className="w-4 h-4" /> Request pending review
              </p>
              <p className="text-xs text-[#8A92A0]">Submitted {status.requested_at ? new Date(status.requested_at).toLocaleDateString() : ""}. Our team reviews within 48 hours.</p>
            </div>
          ) : status.request_status === "rejected" ? (
            <div>
              <p className="text-sm text-[#D14A85] mb-2">Your last request was not approved.</p>
              <button
                onClick={requestVerification}
                disabled={submitting}
                className="px-4 py-2 rounded-lg bg-[#EC008C] text-[#000000] font-semibold hover:bg-[#FF3399] transition-colors text-sm disabled:opacity-60"
                data-testid="resubmit-verification-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Re-submit Request"}
              </button>
            </div>
          ) : (
            <div>
              <p className="text-sm text-[#8A92A0] mb-3">Get the gold checkmark next to your name. Verified artists rank higher in discovery and earn fan trust.</p>
              <button
                onClick={requestVerification}
                disabled={submitting}
                className="px-4 py-2 rounded-lg bg-[#EC008C] text-[#000000] font-semibold hover:bg-[#FF3399] transition-colors text-sm disabled:opacity-60 flex items-center gap-2"
                data-testid="request-verification-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <BadgeCheck className="w-4 h-4" />}
                {submitting ? "Submitting..." : "Request Verification"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerificationCard;
